import React, { useState } from 'react';
import { Save, Mail, Phone, MapPin, Building, Bell, Shield } from 'lucide-react';

const AdminSettings = ({ adminSettings, setAdminSettings, saveAdminSettings }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const updateAdminSettings = (field, value) => {
    setAdminSettings(field, value);
  };

  const handleSaveAdminSettings = async () => {
    // Validation
    if (adminSettings.admin_email && !adminSettings.admin_email.includes('@')) {
      alert('Please enter a valid email address');
      return;
    }
    
    setIsSubmitting(true);
    
    // Settings are already saved automatically via setAdminSettings
    // This is just for user feedback
    setTimeout(() => {
      alert('Settings saved successfully!');
      setIsSubmitting(false);
    }, 500);
  };

  return (
    <div className="space-y-6">
      {/* Business Information */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="bg-blue-100 p-3 rounded-full">
            <Building className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-gray-800">Business Information</h3>
            <p className="text-gray-600">Configure your business details</p>
          </div>
        </div>
        
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Building className="w-4 h-4 inline mr-2" />
              Business Name
            </label>
            <input
              type="text"
              value={adminSettings.business_name || ''}
              onChange={(e) => updateAdminSettings('business_name', e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-green-500 focus:border-transparent"
              placeholder="Your Business Name"
              disabled={isSubmitting}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Phone className="w-4 h-4 inline mr-2" />
              Business Phone
            </label>
            <input
              type="tel"
              value={adminSettings.business_phone || ''}
              onChange={(e) => updateAdminSettings('business_phone', e.target.value)}
              placeholder="(555) 123-4567"
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-green-500 focus:border-transparent"
              disabled={isSubmitting}
            />
          </div>
          
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <MapPin className="w-4 h-4 inline mr-2" />
              Business Address
            </label>
            <textarea
              value={adminSettings.business_address || ''}
              onChange={(e) => updateAdminSettings('business_address', e.target.value)}
              placeholder="123 Main St, City, State 12345"
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-green-500 focus:border-transparent"
              rows="3"
              disabled={isSubmitting}
            />
          </div>
        </div>
      </div>

      {/* Contact & Notifications */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="bg-green-100 p-3 rounded-full">
            <Bell className="w-6 h-6 text-green-600" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-gray-800">Contact & Notifications</h3>
            <p className="text-gray-600">Manage how you receive notifications</p>
          </div>
        </div>
        
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Mail className="w-4 h-4 inline mr-2" />
              Admin Email
            </label>
            <input
              type="email"
              value={adminSettings.admin_email || ''}
              onChange={(e) => updateAdminSettings('admin_email', e.target.value)}
              placeholder="admin@yourcompany.com"
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-green-500 focus:border-transparent"
              disabled={isSubmitting}
            />
            <p className="text-xs text-gray-500 mt-1">You'll receive new appointment notifications here</p>
          </div>
          
          <div className="space-y-4">
            <h4 className="font-semibold text-gray-700">Notification Preferences</h4>
            
            <div className="space-y-3">
              <label className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                <input
                  type="checkbox"
                  checked={adminSettings.email_notifications || false}
                  onChange={(e) => updateAdminSettings('email_notifications', e.target.checked)}
                  className="mr-3 h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                  disabled={isSubmitting}
                />
                <div className="flex-1">
                  <span className="text-sm font-medium text-gray-700">Email Notifications</span>
                  <p className="text-xs text-gray-500">Receive email alerts for new appointments and updates</p>
                </div>
              </label>
              
              <label className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                <input
                  type="checkbox"
                  checked={adminSettings.sms_notifications || false}
                  onChange={(e) => updateAdminSettings('sms_notifications', e.target.checked)}
                  className="mr-3 h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                  disabled={isSubmitting}
                />
                <div className="flex-1">
                  <span className="text-sm font-medium text-gray-700">SMS Notifications</span>
                  <p className="text-xs text-gray-500">Receive text message alerts (requires SMS service setup)</p>
                </div>
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex justify-between items-center">
          <div>
            <h4 className="font-semibold text-gray-800">Save Changes</h4>
            <p className="text-sm text-gray-600">Settings are saved automatically as you type</p>
          </div>
          <button
            onClick={handleSaveAdminSettings}
            className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg flex items-center gap-2 disabled:opacity-50 transition-colors"
            disabled={isSubmitting}
          >
            <Save className="w-4 h-4" />
            {isSubmitting ? 'Saving...' : 'Save All Settings'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;